/* ==========================================================================
   ogai.js — shared behaviour for the Off-Grid AI homepage sections.

   Loaded deferred from layout/theme.liquid on the index template only.

   The previous build gave every snippet its own IIFE that resolved scope
   through document.currentScript. That works while the markup sits exactly
   where it was rendered and breaks the moment anything is moved or injected
   async. One document-level pass instead, driven by data attributes.

   Everything here degrades to "already visible / final value" when
   IntersectionObserver is missing or the visitor prefers reduced motion.
   ========================================================================== */

(function () {
  'use strict';

  var reduce =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ---- reveal on scroll ------------------------------------------------ */

  function initReveal() {
    var targets = document.querySelectorAll('.ogai .reveal, .ogai .reveal-stagger');
    if (!targets.length) return;

    if (reduce || !('IntersectionObserver' in window)) {
      for (var i = 0; i < targets.length; i++) targets[i].classList.add('is-visible');
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );

    for (var j = 0; j < targets.length; j++) io.observe(targets[j]);
  }

  /* ---- count-up -------------------------------------------------------- */
  /* Counts to a real, fixed number and stops. The old build's ticker kept
     incrementing a fabricated "adversarial queries run" figure forever; that
     is gone and is not coming back. */

  function initCounters() {
    var counters = document.querySelectorAll('[data-ogai-count]');
    if (!counters.length) return;

    function render(el, value) {
      var prefix = el.getAttribute('data-ogai-prefix') || '';
      var suffix = el.getAttribute('data-ogai-suffix') || '';
      el.textContent = prefix + Math.round(value).toLocaleString() + suffix;
    }

    function run(el) {
      var target = parseFloat(el.getAttribute('data-ogai-count'));
      if (isNaN(target)) return;

      if (reduce) {
        render(el, target);
        return;
      }

      var duration = 1100;
      var start = null;

      function step(now) {
        if (start === null) start = now;
        var t = Math.min((now - start) / duration, 1);
        render(el, target * (1 - Math.pow(1 - t, 3)));
        if (t < 1) window.requestAnimationFrame(step);
      }

      window.requestAnimationFrame(step);
    }

    if (!('IntersectionObserver' in window)) {
      for (var i = 0; i < counters.length; i++) run(counters[i]);
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          run(entry.target);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.6 }
    );

    for (var j = 0; j < counters.length; j++) io.observe(counters[j]);
  }

  /* ---- typewriter ------------------------------------------------------ */
  /* Cycles the questions in [data-ogai-typed], pipe-separated. */

  function initTyped() {
    var els = document.querySelectorAll('[data-ogai-typed]');

    for (var i = 0; i < els.length; i++) {
      (function (el) {
        var phrases = (el.getAttribute('data-ogai-typed') || '')
          .split('|')
          .map(function (s) { return s.trim(); })
          .filter(Boolean);

        if (!phrases.length) return;

        if (reduce) {
          el.textContent = phrases[0];
          return;
        }

        var index = 0;
        var chars = 0;
        var deleting = false;

        function tick() {
          var phrase = phrases[index];
          chars += deleting ? -1 : 1;
          el.textContent = phrase.slice(0, chars);

          var delay = deleting ? 26 : 52;

          if (!deleting && chars === phrase.length) {
            deleting = true;
            delay = 2000;
          } else if (deleting && chars === 0) {
            deleting = false;
            index = (index + 1) % phrases.length;
            delay = 420;
          }

          window.setTimeout(tick, delay);
        }

        tick();
      })(els[i]);
    }
  }

  /* ---- rotating answer card ------------------------------------------- */
  /* The opening section's Intel card cycles through several real examples
     rather than showing one forever. Every item is in the DOM already, so
     with JS off the visitor still gets a complete first answer.

     Extended 2026-09-03 for Scout, where an example is a photograph AND an
     answer rather than an answer alone. Three optional hooks, all no-ops
     when the markup does not carry them, so the Intel card is untouched:

       [data-ogai-shot]         a photo, matched to the item at the same index
       [data-ogai-rotator-tab]  a label that takes its text from the active
                                item's data-ogai-tab (Scout · Plant, · Wound)
       data-ogai-interval       ms per slide; Scout's answers are longer than
                                Intel's and need the extra reading time */

  function initAnswerRotator() {
    var rotators = document.querySelectorAll('[data-ogai-rotator]');

    for (var i = 0; i < rotators.length; i++) {
      (function (root) {
        var items = root.querySelectorAll('[data-ogai-qa]');
        var shots = root.querySelectorAll('[data-ogai-shot]');
        var dots = root.querySelectorAll('[data-ogai-dot]');
        var tabEl = root.querySelector('[data-ogai-rotator-tab]');
        var dotWrap =
          root.querySelector('[data-ogai-dots]') ||
          root.querySelector('.ogai-prob__dots');
        if (items.length < 2) return;

        function setPausedFlag(isPaused) {
          if (dotWrap) dotWrap.classList.toggle('is-paused', isPaused);
        }

        var current = 0;
        var timer = null;
        var paused = false;
        var userPaused = false;
        var onScreen = true;
        var INTERVAL = parseInt(root.getAttribute('data-ogai-interval'), 10) || 7000;

        /* Items are stacked in one grid cell, so this is a straight class
           swap — the outgoing and incoming answers crossfade together and the
           card never changes height. */
        function goTo(next) {
          if (next === current) return;

          for (var n = 0; n < items.length; n++) {
            var on = n === next;
            items[n].classList.toggle('is-active', on);
            if (on) items[n].removeAttribute('aria-hidden');
            else items[n].setAttribute('aria-hidden', 'true');
          }

          // The photo crossfades on the same tick as the answer, so the pair
          // never disagrees about which example is on screen.
          for (var s = 0; s < shots.length; s++) {
            shots[s].classList.toggle('is-active', s === next);
          }

          if (tabEl) {
            var label = items[next].getAttribute('data-ogai-tab');
            if (label) tabEl.textContent = label;
          }

          for (var d = 0; d < dots.length; d++) {
            var sel = d === next;
            dots[d].setAttribute('aria-current', sel ? 'true' : 'false');
            if (sel) {
              // Force the fill animation to restart from zero.
              var after = dots[d];
              after.style.animation = 'none';
              void after.offsetWidth;
              after.style.animation = '';
            }
          }

          current = next;
        }

        function stop() {
          setPausedFlag(true);
          if (timer === null) return;
          window.clearInterval(timer);
          timer = null;
        }

        function start() {
          if (reduce || paused || userPaused || !onScreen || timer !== null) return;
          setPausedFlag(false);
          timer = window.setInterval(function () {
            goTo((current + 1) % items.length);
          }, INTERVAL);
        }

        /* a11y (WCAG 2.2.2 Pause, Stop, Hide): content that advances on its own
           needs a visible control to stop it, not only hover/focus. The button
           sits in the dot row; reduced-motion visitors never see rotation, so
           they get no button. */
        if (!reduce && dotWrap) {
          var toggle = document.createElement('button');
          toggle.type = 'button';
          toggle.className = 'ogai-motion-toggle';
          toggle.setAttribute('aria-label', 'Pause examples');
          var syncToggle = function () {
            toggle.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
            toggle.innerHTML = userPaused
              ? '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><path d="M8 5v14l11-7z" fill="currentColor"/></svg>'
              : '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><path d="M7 5h4v14H7zM13 5h4v14h-4z" fill="currentColor"/></svg>';
          };
          toggle.addEventListener('click', function () {
            userPaused = !userPaused;
            syncToggle();
            if (userPaused) stop();
          });
          syncToggle();
          dotWrap.appendChild(toggle);
        }

        // Don't advance under someone who is reading, or who has tabbed in.
        root.addEventListener('mouseenter', function () { paused = true; stop(); });
        root.addEventListener('mouseleave', function () { paused = false; start(); });
        root.addEventListener('focusin', function () { paused = true; stop(); });
        root.addEventListener('focusout', function () { paused = false; start(); });

        for (var k = 0; k < dots.length; k++) {
          (function (idx) {
            dots[idx].addEventListener('click', function () {
              stop();
              goTo(idx);
              start();
            });
          })(k);
        }

        // No point cycling a card nobody can see.
        if ('IntersectionObserver' in window) {
          var io = new IntersectionObserver(
            function (entries) {
              onScreen = entries[0].isIntersecting;
              if (onScreen) start();
              else stop();
            },
            { threshold: 0.2 }
          );
          io.observe(root);
        }

        // A hidden tab still fires setInterval; stop when the page is away.
        document.addEventListener('visibilitychange', function () {
          if (document.hidden) stop();
          else start();
        });

        start();
      })(rotators[i]);
    }
  }

  /* ---- marquee pause ---------------------------------------------------- */
  /* a11y (WCAG 2.2.2 Pause, Stop, Hide): the feedback strip scrolls forever.
     Hover and focus already hold it; this adds the visible control the rule
     asks for. Reduced motion stops the strip in CSS, so no button there. */
  function initMarqueePause() {
    if (reduce) return;
    var marquees = document.querySelectorAll('[data-ogai-marquee]');
    for (var i = 0; i < marquees.length; i++) {
      (function (m) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ogai-motion-toggle ogai-motion-toggle--below';
        btn.setAttribute('aria-label', 'Pause customer feedback');
        function sync() {
          var p = m.classList.contains('is-user-paused');
          btn.setAttribute('aria-pressed', p ? 'true' : 'false');
          btn.innerHTML = p
            ? '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><path d="M8 5v14l11-7z" fill="currentColor"/></svg>'
            : '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><path d="M7 5h4v14H7zM13 5h4v14h-4z" fill="currentColor"/></svg>';
        }
        btn.addEventListener('click', function () {
          m.classList.toggle('is-user-paused');
          sync();
        });
        sync();
        m.parentNode.insertBefore(btn, m.nextSibling);
      })(marquees[i]);
    }
  }

  /* ---- tabbed panels --------------------------------------------------- */
  /* Used by the "four ways to use it" section. Keyboard support matters
     here — this is the main navigation of the page's largest block. */

  /* The strip hides its scrollbar, so on a phone the two tabs past the fold
     had no affordance at all. These two helpers are the JS half of the fix;
     the CSS half is the edge-fade block in ogai-four-ways.liquid.

     Deliberately NOT coupled to page scroll. The strip is a tap target, and
     a control that drifts sideways while the thumb is travelling towards it
     produces mistaps on the main navigation of the largest block on the
     page. A fade that stays put says the same thing and stays honest. */

  function syncStripEdges(strip) {
    if (!strip) return;
    var slack = strip.scrollWidth - strip.clientWidth;
    // 1px of tolerance: sub-pixel layout leaves a hair of slack that is not
    // actually scrollable, and without this the fade never clears at the end.
    strip.classList.toggle('is-overflowing', slack > 1);
    strip.classList.toggle('is-at-start', strip.scrollLeft <= 1);
    strip.classList.toggle('is-at-end', strip.scrollLeft >= slack - 1);
  }

  /* Centres a tab within its own strip WITHOUT touching any other scroll
     container. scrollIntoView would have been shorter, but it walks every
     scrollable ancestor and would jump the page vertically on a tab tap.
     Rects rather than offsetLeft, because the strip is not positioned and
     offsetLeft would resolve against something further up the tree. */
  function centreTab(strip, tab, instant) {
    if (!strip || !tab) return;
    var slack = strip.scrollWidth - strip.clientWidth;
    if (slack <= 1) return;

    var t = tab.getBoundingClientRect();
    var box = strip.getBoundingClientRect();
    var target = strip.scrollLeft + (t.left - box.left) - (box.width - t.width) / 2;
    target = Math.max(0, Math.min(target, slack));

    if (strip.scrollTo) {
      strip.scrollTo({ left: target, behavior: instant || reduce ? 'auto' : 'smooth' });
    } else {
      strip.scrollLeft = target;
    }
  }

  /* A single nudge the first time the strip comes into view: slide right a
     little, settle back. Motion is the one cue people reliably read as "this
     moves", and one self-terminating gesture buys that without the problems
     of coupling the strip to page scroll — the tap target ends up exactly
     where it started, and it never fights a thumb.

     Cancelled by any touch, skipped under reduced motion, and skipped when
     the row does not overflow. Snapping is suspended for the duration: with
     scroll-snap active, proximity would fight a 28px move and land it
     somewhere unintended. */
  function nudgeStrip(strip) {
    if (!strip || reduce || !('IntersectionObserver' in window)) return;
    if (strip.scrollWidth - strip.clientWidth <= 1) return;

    var spent = false;
    function spend() { spent = true; }
    strip.addEventListener('touchstart', spend, { passive: true, once: true });
    strip.addEventListener('pointerdown', spend, { passive: true, once: true });

    var io = new IntersectionObserver(function (entries) {
      if (spent || !entries.some(function (e) { return e.isIntersecting; })) return;
      spent = true;
      io.disconnect();

      var home = strip.scrollLeft;
      var snap = strip.style.scrollSnapType;
      strip.style.scrollSnapType = 'none';

      if (!strip.scrollTo) { strip.style.scrollSnapType = snap; return; }
      strip.scrollTo({ left: home + 28, behavior: 'smooth' });
      setTimeout(function () {
        strip.scrollTo({ left: home, behavior: 'smooth' });
        setTimeout(function () { strip.style.scrollSnapType = snap; }, 500);
      }, 420);
    }, { threshold: 0.4 });

    io.observe(strip);
  }

  function initTabs() {
    var groups = document.querySelectorAll('[data-ogai-tabs]');

    for (var i = 0; i < groups.length; i++) {
      (function (group) {
        var tabs = group.querySelectorAll('[role="tab"]');
        var panels = group.querySelectorAll('[role="tabpanel"]');
        var strip = group.querySelector('[role="tablist"]');
        if (!tabs.length) return;

        function select(next, focus, instant) {
          for (var t = 0; t < tabs.length; t++) {
            var on = t === next;
            tabs[t].setAttribute('aria-selected', on ? 'true' : 'false');
            tabs[t].setAttribute('tabindex', on ? '0' : '-1');
            if (panels[t]) panels[t].hidden = !on;
          }
          // preventScroll so the browser's own "bring focus into view" jump
          // does not fire immediately before centreTab scrolls to a different
          // offset, which reads as a stutter when arrowing along the strip.
          // Ignored by browsers that do not support it, which just leaves
          // today's behaviour.
          if (focus) tabs[next].focus({ preventScroll: true });
          centreTab(strip, tabs[next], instant);
        }

        for (var k = 0; k < tabs.length; k++) {
          (function (idx) {
            tabs[idx].addEventListener('click', function () { select(idx, false); });

            tabs[idx].addEventListener('keydown', function (event) {
              var next = null;
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (idx + 1) % tabs.length;
              else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (idx - 1 + tabs.length) % tabs.length;
              else if (event.key === 'Home') next = 0;
              else if (event.key === 'End') next = tabs.length - 1;
              if (next === null) return;
              event.preventDefault();
              select(next, true);
            });
          })(k);
        }

        if (strip) {
          strip.addEventListener('scroll', function () { syncStripEdges(strip); }, { passive: true });
          window.addEventListener('resize', function () { syncStripEdges(strip); });
          syncStripEdges(strip);
        }

        // instant: centring tab 0 clamps to scrollLeft 0 anyway, but going
        // through the smooth path on load would animate nothing, slowly.
        select(0, false, true);
        nudgeStrip(strip);
      })(groups[i]);
    }
  }

  /* ---- sticky buy bar -------------------------------------------------- */
  /* Appears once the hero has scrolled away. All four overlay-group
     sections (popup, cookie banner, age verifier, mobile dock) are disabled
     on this store, so nothing else occupies the bottom of the viewport. */

  function initBuyBar() {
    var bar = document.querySelector('[data-ogai-buybar]');
    if (!bar) return;

    var dismiss = bar.querySelector('[data-ogai-buybar-close]');
    var dismissed = false;

    if (dismiss) {
      dismiss.addEventListener('click', function () {
        dismissed = true;
        bar.classList.remove('is-visible');
      });
    }

    // Hide again over the closing CTA so the bar never covers the very
    // button it is pointing at.
    var footerCta = document.querySelector('.ogai-cta');

    function update() {
      if (dismissed) return;

      var pastHero = window.scrollY > window.innerHeight * 0.9;
      var overFooter = false;

      if (footerCta) {
        var rect = footerCta.getBoundingClientRect();
        overFooter = rect.top < window.innerHeight && rect.bottom > 0;
      }

      bar.classList.toggle('is-visible', pastHero && !overFooter);
    }

    var ticking = false;

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          update();
          ticking = false;
        });
      },
      { passive: true }
    );

    update();
  }

  onReady(function () {
    initReveal();
    initCounters();
    initTyped();
    initAnswerRotator();
    initMarqueePause();
    initTabs();
    initBuyBar();
  });
})();
